import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import './DashboardSelector.css';
import { useDashboardContext } from '../hooks/useDashboardContext';
import DashboardService from '../services/DashboardService';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * 대시보드 선택 컴포넌트
 * 사용자가 여러 대시보드 중에서 선택할 수 있는 드롭다운 UI를 제공합니다.
 */
const DashboardSelector = ({
  className = '',
  onDashboardTitleChange
}) => {
  const [dashboards, setDashboards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [currentDashboardId, setCurrentDashboardId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false); // 관리자 여부 상태 추가
  const dropdownRef = useRef(null);

  // 컨텍스트에서 필요한 함수들 가져오기
  const { dashboardActions, editModeActions, dashboardTemplate, registerDashboardListActions, unregisterDashboardListActions, updateDashboardList } = useDashboardContext();
  const reloadDashboardTemplate = dashboardActions?.reloadDashboardTemplate;
  const editMode = editModeActions?.editMode; // 편집 모드 상태 가져오기
  
  // 디버깅: editMode 값 확인
  // 컨텍스트에서 현재 로드된 대시보드 정보와 동기화
  useEffect(() => {
    if (dashboardTemplate && dashboardTemplate.dashboard_id) {
      const actualDashboardId = dashboardTemplate.dashboard_id;
      // 현재 UI 상태와 실제 로드된 대시보드가 다르면 동기화
      if (currentDashboardId !== actualDashboardId) {
        setCurrentDashboardId(actualDashboardId);
        
        // 제목도 업데이트
        if (onDashboardTitleChange && dashboardTemplate.title) {
          onDashboardTitleChange(dashboardTemplate.title);
        }
      } else if (currentDashboardId === actualDashboardId && onDashboardTitleChange && dashboardTemplate.title) {
        // 같은 대시보드여도 제목이 변경되었을 수 있으므로 업데이트
        onDashboardTitleChange(dashboardTemplate.title);
        
        // dashboards 목록에서도 제목 업데이트
        setDashboards(prevDashboards => 
          prevDashboards.map(d => 
            d.dashboard_id === actualDashboardId 
              ? { ...d, title: dashboardTemplate.title }
              : d
          )
        );
      }
    }
  }, [dashboardTemplate, onDashboardTitleChange]); // currentDashboardId 의존성 제거하여 무한 리렌더링 방지

  // 백엔드에서 마지막 접근 대시보드를 관리하므로 쿠키 관련 로직 제거

  // 대시보드 목록이 로드된 후 동기화 확인
  useEffect(() => {
    if (dashboards.length > 0 && currentDashboardId) {
      const foundDashboard = dashboards.find(d => d.dashboard_id === currentDashboardId);
      if (!foundDashboard) {
        // 대시보드 목록이 있으면 첫 번째 대시보드로 UI 상태 설정
        if (dashboards[0]) {
          const firstDashboard = dashboards[0];
          setCurrentDashboardId(firstDashboard.dashboard_id);
          
          // 제목 업데이트
          if (onDashboardTitleChange) {
            onDashboardTitleChange(firstDashboard.title);
          }
          
          // reloadDashboardTemplate이 사용 가능하면 사용, 아니면 페이지 새로고침
          if (reloadDashboardTemplate) {
            reloadDashboardTemplate(firstDashboard.dashboard_id).catch(error => {
              window.location.reload();
            });
          } else {
            // reloadDashboardTemplate이 아직 준비되지 않았으면 페이지 새로고침
            window.location.reload();
          }
        }
      } else {
      }
    }
  }, [dashboards]); // 의존성 최소화하여 무한 루프 방지

  /**
   * 현재 선택된 대시보드 정보 반환
   */
  const getCurrentDashboard = useCallback(() => {
    if (!currentDashboardId || dashboards.length === 0) {
      return dashboards[0] || null;
    }
    
    const foundDashboard = dashboards.find(d => d.dashboard_id === currentDashboardId);
    if (foundDashboard) {
      return foundDashboard;
    }
    
    // 쿠키의 대시보드 ID가 목록에 없으면 첫 번째 반환
    return dashboards[0] || null;
  }, [currentDashboardId, dashboards]);

  // 현재 대시보드 제목 변경 시 상위 컴포넌트에 알림
  useEffect(() => {
    const currentDashboard = getCurrentDashboard();
    if (currentDashboard && onDashboardTitleChange) {
      onDashboardTitleChange(currentDashboard.title);
    }
  }, [getCurrentDashboard, onDashboardTitleChange]);

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  /**
   * 사용자의 대시보드 목록을 서버에서 로드합니다.
   */
  const loadUserDashboards = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // 편집 모드에 따라 적절한 mode 파라미터 설정
      // editMode가 undefined일 경우 기본값은 'runtime'
      const mode = editMode === true ? 'edit' : 'runtime';
      const response = await axios.get(`${API_URL}/visualization/user-dashboards/`, {
        params: { mode },
        withCredentials: true
      });

      if (response.data && response.data.dashboards) {
        setDashboards(response.data.dashboards);

        // 관리자 여부 저장
        if (response.data.is_admin !== undefined) {
          setIsAdmin(response.data.is_admin);
          console.log('[DashboardSelector] Is Admin:', response.data.is_admin);
        }

        // 숨겨진 대시보드 디버깅
        const hiddenDashboards = response.data.dashboards.filter(d => d.is_hidden_in_list);
        console.log('[DashboardSelector] Hidden dashboards:', hiddenDashboards.map(d => d.title));
        console.log('[DashboardSelector] Edit Mode:', editMode, 'Is Admin:', response.data.is_admin);

        // 컨텍스트에도 대시보드 목록 업데이트
        if (updateDashboardList) {
          updateDashboardList(response.data.dashboards);
        }
      }
    } catch (err) {
      setError('대시보드 목록을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [editMode, updateDashboardList]);

  // 편집 모드 변경 시 대시보드 목록 다시 로드
  useEffect(() => {
    // 초기 마운트 시 또는 editMode 변경 시 로드
    loadUserDashboards();
  }, [editMode, loadUserDashboards]); // editMode와 loadUserDashboards 의존성

  // 컨텍스트에 대시보드 목록 액션 등록 및 window 객체에 직접 등록
  useEffect(() => {
    const dashboardListActions = {
      refreshDashboardList: loadUserDashboards,
      setCurrentDashboardId: setCurrentDashboardId  // 현재 대시보드 ID 업데이트 함수 추가
    };
    
    registerDashboardListActions(dashboardListActions);
    
    // window 객체에도 직접 등록 (editMode 변경 시마다 최신 함수로 업데이트)
    window.refreshDashboardList = loadUserDashboards;
    // 컴포넌트 언마운트 시 등록 해제
    return () => {
      unregisterDashboardListActions();
      if (window.refreshDashboardList === loadUserDashboards) {
        delete window.refreshDashboardList;
      }
    };
  }, [registerDashboardListActions, unregisterDashboardListActions, loadUserDashboards, editMode]); // editMode 추가

  /**
   * 대시보드 선택 처리
   */
  const handleDashboardSelect = async (dashboard) => {
    setDropdownOpen(false);
    // 현재 선택된 대시보드와 다른 경우에만 변경
    if (currentDashboardId !== dashboard.dashboard_id) {
      try {
        // UI 상태 업데이트 (백엔드에서 마지막 접근 관리)
        setCurrentDashboardId(dashboard.dashboard_id);
        
        // 제목 즉시 업데이트
        if (onDashboardTitleChange) {
          onDashboardTitleChange(dashboard.title);
        }
        
        // 컨텍스트에서 reloadDashboardTemplate 함수를 사용하여 새 템플릿 로드
        if (reloadDashboardTemplate) {
          const result = await reloadDashboardTemplate(dashboard.dashboard_id);
          
          if (result.success) {
          } else {
            // 실패 시 페이지 새로고침으로 폴백
            window.location.reload();
          }
        } else {
          // 컨텍스트가 준비되지 않았으면 페이지 새로고침으로 폴백
          window.location.reload();
        }
      } catch (error) {
        // 오류 발생 시 페이지 새로고침으로 폴백
        window.location.reload();
      }
    }
  };

  /**
   * 새로운 빈 대시보드 템플릿 생성
   */
  const createBlankDashboardTemplate = () => {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const newDashboardId = `dashboard_${timestamp}`;
    
    return {
      dashboardId: newDashboardId,
      dashboard_id: newDashboardId,
      title: '새 대시보드',
      components: [],
      layout: {
        rows: 12,
        columns: 12
      },
      dashboard_options: {
        // 실제 렌더 경로에서 사용하는 키만 포함 (snake_case)
        background_color: '#f5f5f5',
        grid_spacing: 10,
        padding: { top: 20, right: 20, bottom: 20, left: 20 },
        header: {
          show: true,
          height: 60,
          background_color: '#ffffff',
          text_color: '#333333'
        }
      }
    };
  };

  /**
   * 새 대시보드 추가 처리
   */
  const handleAddNewDashboard = async () => {
    setDropdownOpen(false);
    
    try {
      // 1. 빈 대시보드 템플릿 생성
      const newDashboardTemplate = createBlankDashboardTemplate();
      
      // 2. 백엔드에 저장
      const response = await DashboardService.saveDashboardWithOptions(
        newDashboardTemplate,
        { 
          generate_new_id: true,
          current_dashboard_id: currentDashboardId
        }
      );
      
      if (response.success) {
        const savedDashboard = response.data?.dashboard_template || newDashboardTemplate;
        const newDashboardId = savedDashboard.dashboard_id || savedDashboard.dashboardId;
        
        // 3. 대시보드 목록 새로고침
        await loadUserDashboards();
        
        // 4. 새 대시보드로 전환
        if (reloadDashboardTemplate) {
          const loadResult = await reloadDashboardTemplate(newDashboardId);
          
          if (loadResult.success) {
            // 5. 대시보드가 성공적으로 로드된 후 편집 모드 활성화 및 대시보드 옵션 모달 열기
            // 약간의 지연을 두어 UI가 완전히 렌더링되도록 함
            setTimeout(() => {
              const toggleEditMode = editModeActions?.toggleEditMode;
              const openOptionsModal = editModeActions?.openOptionsModal;
              const editMode = editModeActions?.editMode;
              
              if (toggleEditMode && !editMode) {
                toggleEditMode();
                
                // 편집 모드 활성화 후 대시보드 옵션 모달 열기
                setTimeout(() => {
                  if (openOptionsModal) {
                    openOptionsModal();
                  } else {
                  }
                }, 200); // 편집 모드 UI 렌더링 완료 후 모달 열기
              }
            }, 100); // UI 렌더링을 위한 최소 지연
          }
        } else {
          // reloadDashboardTemplate이 없으면 페이지 새로고침
          window.location.reload();
        }
      } else {
        alert('새 대시보드 생성에 실패했습니다.');
      }
    } catch (error) {
      alert('새 대시보드 생성 중 오류가 발생했습니다.');
    }
  };

  /**
   * 대시보드 삭제 처리
   */
  const handleDashboardDelete = async (dashboard, event) => {
    // 이벤트 버블링 방지 (드롭다운 닫힘 방지)
    event.stopPropagation();
    
    if (!window.confirm(`"${dashboard.title}" 대시보드를 정말 삭제하시겠습니까?\n\n이 작업은 되돌릴 수 없습니다.`)) {
      return;
    }
    
    try {
      const response = await DashboardService.deleteDashboard(dashboard.dashboard_id);
      
      if (response.success) {
        // 삭제된 대시보드가 현재 선택된 대시보드인 경우 처리
        if (currentDashboardId === dashboard.dashboard_id) {
          // 다른 대시보드로 전환하거나 새로고침
          const remainingDashboards = dashboards.filter(d => d.dashboard_id !== dashboard.dashboard_id);
          if (remainingDashboards.length > 0) {
            // 첫 번째 남은 대시보드로 전환
            const firstDashboard = remainingDashboards[0];
            setCurrentDashboardId(firstDashboard.dashboard_id);
            if (onDashboardTitleChange) {
              onDashboardTitleChange(firstDashboard.title);
            }
            
            if (reloadDashboardTemplate) {
              await reloadDashboardTemplate(firstDashboard.dashboard_id);
            } else {
              window.location.reload();
            }
          } else {
            // 마지막 대시보드가 삭제된 경우 페이지 새로고침
            window.location.reload();
          }
        }
        
        // 대시보드 목록 새로고침
        await loadUserDashboards();
        
      } else {
        alert(`대시보드 삭제에 실패했습니다: ${response.error}`);
      }
      
    } catch (error) {
      alert('대시보드 삭제 중 오류가 발생했습니다.');
    }
  };

  /**
   * 날짜 포맷팅 함수
   */
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 대시보드가 없는 경우에만 선택기를 표시하지 않음
  if (dashboards.length === 0) {
    return null;
  }

  const currentDashboard = getCurrentDashboard();

  return (
    <div className={`dashboard-selector ${className}`} ref={dropdownRef}>
      <div
        className="dashboard-selector-trigger"
        onClick={() => setDropdownOpen(!dropdownOpen)}
      >
        <div className="current-dashboard-info">
          <div
            className="dashboard-title"
            title={currentDashboard?.title || '대시보드 선택'}
            style={currentDashboard?.is_hidden_in_list ? {
              color: '#9ca3af'
            } : {}}
          >
            {currentDashboard?.title || '대시보드 선택'}
            {currentDashboard?.is_hidden_in_list && (editMode || isAdmin) && (
              <span
                className="hidden-badge-trigger"
                title="런타임 모드에서 숨겨짐 (팝업용)"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  marginLeft: '6px',
                  padding: '2px 6px',
                  fontSize: '10px',
                  backgroundColor: '#e5e7eb',
                  color: '#6b7280',
                  borderRadius: '3px',
                  fontWeight: '600',
                  border: '1px solid #d1d5db'
                }}
              >팝업</span>
            )}
          </div>
        </div>
        <span className={`dropdown-arrow ${dropdownOpen ? 'open' : ''}`}>▼</span>
      </div>

      {dropdownOpen && (
        <div className="dashboard-dropdown">
          {loading && (
            <div className="dropdown-loading">로딩 중...</div>
          )}
          
          {error && (
            <div className="dropdown-error">{error}</div>
          )}
          
          {!loading && !error && (
            <>
              {dashboards.map((dashboard) => {
                return (
                  <div
                    key={dashboard.dashboard_id}
                    className={`dashboard-option ${currentDashboardId === dashboard.dashboard_id ? 'active' : ''} ${dashboard.is_hidden_in_list ? 'hidden-dashboard' : ''}`}
                    onClick={() => handleDashboardSelect(dashboard)}
                  >
                  <div className="dashboard-option-content">
                    <div className="dashboard-option-header">
                      <div className="dashboard-option-title" title={dashboard.title}>
                        {dashboard.title}
                        {dashboard.is_hidden_in_list && (editMode || isAdmin) && (
                          <span
                            className="hidden-badge"
                            title="런타임 모드에서 숨겨짐 (팝업용)"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              marginLeft: '8px',
                              padding: '2px 6px',
                              fontSize: '10px',
                              backgroundColor: '#e5e7eb',
                              color: '#6b7280',
                              borderRadius: '3px',
                              fontWeight: '600',
                              border: '1px solid #d1d5db'
                            }}
                          >팝업</span>
                        )}
                      </div>
                      <div className="dashboard-actions">
                        {currentDashboardId === dashboard.dashboard_id && (
                          <span className="check-mark">✓</span>
                        )}
                        {/* 대시보드가 2개 이상일 때만 삭제 버튼 표시 */}
                        {dashboards.length > 1 && (
                          <button
                            className="delete-dashboard-btn"
                            onClick={(e) => handleDashboardDelete(dashboard, e)}
                            title={`"${dashboard.title}" 삭제`}
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="dashboard-option-meta">
                      <div className="dashboard-updated">
                        {formatDate(dashboard.updated_at)}
                      </div>
                    </div>
                  </div>
                </div>
              );
              })}
              
              {/* 새 대시보드 추가 옵션 */}
              <div className="dashboard-option add-new-dashboard" onClick={handleAddNewDashboard}>
                <div className="add-new-content">
                  <span className="add-icon">+</span>
                  <span className="add-text">새 대시보드 추가</span>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default DashboardSelector; 